'use client'

import { Fragment, memo, useMemo, type ReactNode } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import { QuestionChart, type ChartSpec } from './QuestionChart'
import Diagram from '@/components/lesson/Diagram'
import { parseTutorDiagramSpec } from '@/lib/tutor/tutor-diagram'
import { parseStem, parseMathText, speakMathSegment } from '@unlimitedtests/core'
import { splitMarkdownTables, type MarkdownTable } from '@/lib/render/markdown-table'
import { unescapeDisplayText } from '@/lib/render/display-escapes'
import { splitMarkdownBold } from '@/lib/render/markdown-bold'

// Stems are authored (LLM-generated + admin-edited), but they still flow into
// dangerouslySetInnerHTML, so we sanitize before rendering. Allow only text
// formatting + table tags: charts/diagrams use their own ```chart/```diagram
// fenced blocks (parsed out below), so a stem never legitimately needs
// img/a/iframe/script/style. DOMPurify strips everything else — including
// event handlers (onerror, …) and javascript: URLs.
const STEM_SANITIZE = {
  ALLOWED_TAGS: [
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'del',
    'ins',
    'mark',
    'small',
    'sub',
    'sup',
    'code',
    'br',
    'span',
    'p',
    'div',
    'ul',
    'ol',
    'li',
    'blockquote',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'caption',
  ],
  ALLOWED_ATTR: ['class', 'colspan', 'rowspan', 'scope'],
}

function sanitizeHtml(s: string): string {
  return DOMPurify.sanitize(s, STEM_SANITIZE)
}

// Matches both ```chart and ```diagram fenced blocks. Group 1 = language
// (chart|diagram), group 2 = the JSON body. `chart` renders a recharts
// QuestionChart; `diagram` reuses the lesson/tutor <Diagram> dispatcher
// (function graphs, number lines, triangles, etc.).
const BLOCK_RE = /```(chart|diagram)\n([\s\S]*?)```/g

interface StemRendererProps {
  stem: string
  /**
   * Override the wrapper typography. The `question-stem` class is ALWAYS
   * applied (the Runner's `force-light` CSS hooks onto it); this only swaps
   * the size/leading. Defaults to the Runner's `text-base leading-relaxed`;
   * practice/review surfaces pass `text-lg`/`text-sm` to match their layout.
   */
  className?: string
  /**
   * When true, embedded charts render with the fixed light palette + white
   * card (the Runner is on light "paper", so charts must stay light no matter
   * the app theme). Off by default — practice/review/drills surfaces follow
   * the app theme. Forwarded to <QuestionChart>.
   */
  forceLight?: boolean
  /**
   * Render **bold** spans as <strong>. Off by default (SAT stems never use
   * it, and a stem's tested portion is __underlined__, not bolded). The ACT
   * passage surfaces opt in: Science passages bold their viewpoint and study
   * labels ("**Scientist 1**"). See lib/render/markdown-bold.ts.
   */
  markdownBold?: boolean
}

type Segment =
  | { type: 'text'; content: string }
  | { type: 'chart'; content: string }
  | { type: 'diagram'; content: string }

/**
 * Render a plain-text run as escaped React nodes: caret exponents become
 * <sup> (2^x → 2ˣ, m^(1/3) → mᵐ — same transform as <MathText>, see
 * @unlimitedtests/core), and hard newlines become <br/>. Using React
 * nodes (not dangerouslySetInnerHTML) means the text is auto-escaped, so
 * "x < 5" renders literally without an explicit entity pass.
 */
function renderMathTextNodes(text: string, keyPrefix: string, bold = false): ReactNode[] {
  if (bold && text.includes('**')) {
    return splitMarkdownBold(text).map((part, i) =>
      part.bold ? (
        <strong key={`${keyPrefix}-b${i}`}>
          {renderMathTextNodes(part.text, `${keyPrefix}-b${i}`)}
        </strong>
      ) : (
        <Fragment key={`${keyPrefix}-b${i}`}>
          {renderMathTextNodes(part.text, `${keyPrefix}-b${i}`)}
        </Fragment>
      ),
    )
  }
  return parseMathText(text).map((seg, i) => {
    // See MathText.tsx: adds a spoken form without adding a text node, so
    // persisted highlight offsets (computed from textContent) do not move.
    if (seg.sup)
      return (
        <sup key={`${keyPrefix}-${i}`} role="img" aria-label={speakMathSegment(seg.text)}>
          {seg.text}
        </sup>
      )
    const lines = seg.text.split('\n')
    return (
      <Fragment key={`${keyPrefix}-${i}`}>
        {lines.map((line, k) => (
          <Fragment key={k}>
            {k > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </Fragment>
    )
  })
}

/**
 * Render a plain-text stem segment, honoring the `__underlined__` markup
 * (Digital SAT Writing & Language convention — the choices replace the
 * underlined portion; see @unlimitedtests/core) AND caret-exponent
 * math (so practice/review surfaces that switched from <MathText> to this
 * renderer keep their x^2 → x² rendering).
 *
 * Tag-bearing stems (tables, <p>, sub/sup, …) take the sanitized-HTML path —
 * they're authored with explicit markup and don't flow through the
 * caret/underline transforms (matches long-standing Runner behavior). The
 * vast majority of caret-notation stems are plain text, so this covers the
 * #711 class without DOM-walking sanitized HTML.
 */
const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i

/**
 * Render a parsed markdown pipe table as a real bordered table (cells honor
 * caret-exponent math). Question stems express two-way / data tables this way
 * (e.g. Probability), and without this they leaked through as raw "| a | b |"
 * text. HTML <table> stems take the sanitize path above; this covers the
 * markdown form.
 */
function MarkdownTableEl({
  table,
  keyPrefix,
  bold,
}: {
  table: MarkdownTable
  keyPrefix: string
  bold: boolean
}) {
  const hasHeader = table.headers.some((h) => h.trim() !== '')
  // A two-way table's first column names each row ("Juniors", "Seniors"). Mark
  // it as a row header so a screen reader announces "Juniors, Yes: 12" while
  // moving through cells, not a bare "12". Only when every first cell is a
  // non-numeric label — a plain data column stays an ordinary cell.
  const rowHeaders =
    hasHeader &&
    table.rows.length > 0 &&
    table.rows.every((r) => {
      const c = (r[0] ?? '').trim()
      return c !== '' && !Number.isFinite(Number(c.replace(/[,$%]/g, '')))
    })
  return (
    <div className="my-3 max-w-full overflow-x-auto">
      <table className="border-collapse text-[0.95em]">
        {hasHeader && (
          <thead>
            <tr>
              {table.headers.map((h, i) => (
                <th
                  key={i}
                  scope="col"
                  className="border border-border px-3 py-1.5 text-left font-semibold"
                >
                  {renderMathTextNodes(h, `${keyPrefix}-h${i}`, bold)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((c, ci) =>
                ci === 0 && rowHeaders ? (
                  // Same look as a data cell; only the semantics change.
                  <th
                    key={ci}
                    scope="row"
                    className="border border-border px-3 py-1.5 text-left font-normal"
                  >
                    {renderMathTextNodes(c, `${keyPrefix}-r${r}c${ci}`, bold)}
                  </th>
                ) : (
                  <td key={ci} className="border border-border px-3 py-1.5">
                    {renderMathTextNodes(c, `${keyPrefix}-r${r}c${ci}`, bold)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TextSegment({ content: raw, bold = false }: { content: string; bold?: boolean }) {
  // Leaked source escapes ("\$85", a literal "\n") are never meant to be
  // seen — see lib/render/display-escapes.ts. Text runs only: a ```chart /
  // ```diagram body is JSON, where "\n" and "\u00b0" are real escapes.
  const content = unescapeDisplayText(raw)
  const parts = parseStem(content)
  const hasUnderline = parts.some((p) => p.type === 'underline')

  // No underline markup — render the run in one pass. Tag-bearing stems
  // (tables, block HTML) take the sanitized-HTML div (identical to the old
  // behavior); tag-free stems get markdown-table + caret-exponent + newline
  // transforms.
  if (!hasUnderline) {
    if (HTML_TAG_RE.test(content)) {
      return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
    }
    const mdSegs = splitMarkdownTables(content)
    if (mdSegs.length === 1 && mdSegs[0].type === 'text') {
      return <div>{renderMathTextNodes(content, 't', bold)}</div>
    }
    return (
      <div>
        {mdSegs.map((s, i) =>
          s.type === 'table' ? (
            <MarkdownTableEl key={i} table={s.table} keyPrefix={`mt${i}`} bold={bold} />
          ) : (
            <Fragment key={i}>{renderMathTextNodes(s.text, `t${i}`, bold)}</Fragment>
          ),
        )}
      </div>
    )
  }

  // Underline markup present (W&L). Render each part: tag-free parts get the
  // caret/newline transforms, parts with inline tags are sanitized. Preserves
  // the underline even when a part also carries HTML formatting.
  return (
    <div>
      {parts.map((p, j) => {
        const inner = HTML_TAG_RE.test(p.text) ? (
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.text) }} />
        ) : (
          renderMathTextNodes(p.text, `${p.type}${j}`, bold)
        )
        return p.type === 'underline' ? (
          <u key={j} className="underline decoration-2 underline-offset-4">
            {inner}
          </u>
        ) : (
          <span key={j}>{inner}</span>
        )
      })}
    </div>
  )
}

/**
 * A chart segment, memoized on its raw JSON so it survives the runner's 250ms
 * timer re-render. Parsing inline in StemRenderer's body produced a brand-new
 * `spec` object every render (~4×/sec), which recharts read as new data and
 * re-laid-out/re-animated — the visible x-axis "shiver". useMemo keyed on the
 * (stable) JSON string yields a stable object identity, and memo() keeps the
 * whole chart from re-rendering unless the question actually changes.
 */
const ChartSegment = memo(function ChartSegment({
  content,
  forceLight,
}: {
  content: string
  forceLight: boolean
}) {
  const spec = useMemo<ChartSpec | null>(() => {
    try {
      return JSON.parse(content) as ChartSpec
    } catch {
      return null
    }
  }, [content])
  if (!spec) {
    return <div className="text-sm text-red-500">[Chart data could not be parsed]</div>
  }
  return <QuestionChart spec={spec} forceLight={forceLight} />
})

export function StemRenderer({
  stem,
  className,
  forceLight = false,
  markdownBold = false,
}: StemRendererProps) {
  const wrapperClass = `question-stem ${className ?? 'text-base leading-relaxed'}`

  // Split stem into text / chart / diagram segments, in order.
  const segments: Segment[] = []
  let lastIndex = 0

  // Reset regex state
  BLOCK_RE.lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = BLOCK_RE.exec(stem)) !== null) {
    // Text before the block
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: stem.slice(lastIndex, match.index) })
    }
    const lang = match[1] as 'chart' | 'diagram'
    segments.push({ type: lang, content: match[2] })
    lastIndex = match.index + match[0].length
  }

  // Remaining text after the last block
  if (lastIndex < stem.length) {
    segments.push({ type: 'text', content: stem.slice(lastIndex) })
  }

  // No chart/diagram blocks — render the whole stem as one text segment
  // (still honors __underline__ markup).
  if (segments.length === 0) {
    return (
      <div className={wrapperClass}>
        <TextSegment content={stem} bold={markdownBold} />
      </div>
    )
  }

  return (
    <div className={wrapperClass}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return <TextSegment key={i} content={seg.content} bold={markdownBold} />
        }

        if (seg.type === 'chart') {
          return <ChartSegment key={i} content={seg.content} forceLight={forceLight} />
        }

        // diagram — reuse the tutor/lesson parser + dispatcher. Parse
        // failures (bad JSON, unsupported type) fail closed to a notice
        // rather than crashing the runner.
        const result = parseTutorDiagramSpec(seg.content)
        if (result.ok) {
          return <Diagram key={i} diagram={result.diagram} />
        }
        return (
          <div key={i} className="text-sm text-red-500">
            [Diagram could not be parsed]
          </div>
        )
      })}
    </div>
  )
}
